import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static targets = [
    "query",
    "results",
    "loading",
    "empty",
    "error",
    "errorMessage",
    "more"
  ]

  static values = {
    url: String
  }

  connect() {
    this.loadedQuery = null
    this.nextOffset = 0
    this.markOpen = () => { this.modalOpen = true }
    this.element.addEventListener("show.bs.modal", this.markOpen)
  }

  disconnect() {
    this.cleanup()
    this.element.removeEventListener("show.bs.modal", this.markOpen)
  }

  activate(commentFormController) {
    this.commentFormController = commentFormController
  }

  shown() {
    this.queryTarget.focus()
    if (this.loadedQuery === null) this.fetchGifs({query: "", offset: 0})
  }

  hidden() {
    this.modalOpen = false
    this.cancelPendingWork()
  }

  cleanup() {
    this.cancelPendingWork()
    const modal = Modal.getInstance(this.element)
    if (!modal) return

    // Finish Bootstrap's transitions before Turbo snapshots the body scroll state.
    const backdrop = this.modalOpen ? Array.from(document.querySelectorAll(".modal-backdrop")).pop() : null
    backdrop?.dispatchEvent(new Event("transitionend"))
    this.element.querySelector(".modal-dialog")?.dispatchEvent(new Event("transitionend"))
    modal.hide()
    this.element.dispatchEvent(new Event("transitionend"))
    backdrop?.dispatchEvent(new Event("transitionend"))
    modal.dispose()
    this.modalOpen = false
  }

  search() {
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => {
      this.fetchGifs({query: this.currentQuery, offset: 0})
    }, 350)
  }

  submitSearch(event) {
    event.preventDefault()
    clearTimeout(this.searchTimer)
    this.fetchGifs({query: this.currentQuery, offset: 0})
  }

  retry() {
    this.fetchGifs(this.lastRequest || {query: this.currentQuery, offset: 0})
  }

  loadMore() {
    this.fetchGifs({
      query: this.loadedQuery || "",
      offset: this.nextOffset,
      append: true
    })
  }

  select(event) {
    const gif = JSON.parse(event.currentTarget.dataset.gif)
    this.commentFormController?.selectGif(gif)
  }

  async fetchGifs({query, offset, append = false}) {
    this.requestController?.abort()
    this.requestController = new AbortController()
    this.lastRequest = {query, offset, append}
    this.showLoading(append)

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", query)
      url.searchParams.set("offset", offset)

      const response = await fetch(url, {
        headers: {"Accept": "application/json"},
        credentials: "same-origin",
        signal: this.requestController.signal
      })
      const payload = await response.json()

      if (!response.ok) throw new Error(payload.error)

      this.renderResults(payload.gifs || [], append)
      this.loadedQuery = query
      this.nextOffset = payload.pagination.offset + payload.pagination.count
      this.toggleMore(this.nextOffset < payload.pagination.total_count)
    } catch (error) {
      if (error.name === "AbortError") return

      this.showError(error.message)
    }
  }

  renderResults(gifs, append) {
    if (!append) this.resultsTarget.replaceChildren()
    gifs.forEach((gif) => this.resultsTarget.appendChild(this.gifButton(gif)))

    this.hideStates()
    this.emptyTarget.classList.toggle("d-none", this.resultsTarget.children.length > 0)
  }

  gifButton(gif) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "giphy-picker-result"
    button.dataset.action = "giphy-picker#select"
    button.dataset.bsDismiss = "modal"
    button.dataset.gif = JSON.stringify(gif)
    button.setAttribute("aria-label", gif.alt_text || gif.title)

    const image = document.createElement("img")
    image.src = gif.preview_url
    image.alt = gif.alt_text || gif.title
    image.loading = "lazy"
    image.decoding = "async"

    button.appendChild(image)
    return button
  }

  showLoading(append) {
    this.hideStates()
    this.toggleMore(false)
    if (!append) {
      this.resultsTarget.replaceChildren()
      this.loadingTarget.classList.remove("d-none")
    }
  }

  showError(message) {
    this.hideStates()
    this.toggleMore(false)
    this.errorMessageTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
  }

  hideStates() {
    this.loadingTarget.classList.add("d-none")
    this.emptyTarget.classList.add("d-none")
    this.errorTarget.classList.add("d-none")
  }

  toggleMore(show) {
    this.moreTarget.classList.toggle("d-none", !show)
  }

  cancelPendingWork() {
    clearTimeout(this.searchTimer)
    this.requestController?.abort()
  }

  get currentQuery() {
    return this.queryTarget.value.trim()
  }
}
